import { useState } from 'react';

interface StylePreviewGalleryProps {
  selectedStyle: string;
  onSelectStyle: (style: string) => void;
  darkMode: boolean;
}

const STYLE_PREVIEWS = [
  {
    id: 'Realistic',
    label: 'Realistic',
    description: 'True-to-life footage with natural colors and authentic textures',
    icon: 'ri-camera-lens-line',
    image: 'https://public.readdy.ai/ai/img_res/d5bdeaa5b44d8126d88abe471a832bf4.jpg',
    badge: 'Lifelike',
    badgeStyle: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  },
  {
    id: 'Cinematic',
    label: 'Cinematic',
    description: 'Hollywood-grade drama with moody lighting & epic compositions',
    icon: 'ri-film-line',
    image: 'https://readdy.ai/api/search-image?query=cinematic%20epic%20movie%20scene%20dramatic%20moody%20teal%20orange%20color%20grading%20wide%20angle%20lens%20film%20production%20high%20quality%20dramatic%20shadows%20hero%20protagonist%20silhouette%20epic%20scale&width=640&height=360&seq=style-cinematic-001&orientation=landscape',
    badge: 'Most Popular',
    badgeStyle: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  },
  {
    id: 'Animated',
    label: 'Animated',
    description: 'Colorful 3D or 2D animation with smooth fluid motion',
    icon: 'ri-play-circle-line',
    image: 'https://readdy.ai/api/search-image?query=colorful%20vibrant%203d%20animated%20movie%20scene%20bright%20colors%20smooth%20character%20design%20pixar%20disney%20style%20magical%20fantasy%20world%20beautiful%20rim%20lighting%20cel%20shading%20whimsical&width=640&height=360&seq=style-animated-001&orientation=landscape',
    badge: 'Fun & Bright',
    badgeStyle: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400',
  },
  {
    id: 'Abstract',
    label: 'Abstract',
    description: 'Artistic visualizations with flowing shapes and surreal effects',
    icon: 'ri-bubble-chart-line',
    image: 'https://readdy.ai/api/search-image?query=abstract%20geometric%20flowing%20shapes%20vibrant%20gradient%20colors%20digital%20art%20modern%20data%20visualization%20surreal%20neon%20light%20particles%20waves%20smooth%20motion%20luxury%20artistic%20elegant&width=640&height=360&seq=style-abstract-001&orientation=landscape',
    badge: 'Artistic',
    badgeStyle: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  },
];

export default function StylePreviewGallery({ selectedStyle, onSelectStyle, darkMode }: StylePreviewGalleryProps) {
  const [hoveredStyle, setHoveredStyle] = useState<string | null>(null);

  const activeStyle = hoveredStyle ?? selectedStyle;
  const activePreview = STYLE_PREVIEWS.find(s => s.id === activeStyle) ?? STYLE_PREVIEWS[0];

  return (
    <div className={`rounded-2xl border overflow-hidden ${
      darkMode
        ? 'bg-gray-800 border-gray-700'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <span className={`w-5 h-5 flex items-center justify-center ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
            <i className="ri-palette-line text-base"></i>
          </span>
          Visual Style Preview
        </h3>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
          darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
        }`}>
          Click to select
        </span>
      </div>

      {/* Big preview image */}
      <div className="relative mx-5 mb-4 rounded-xl overflow-hidden" style={{ height: '180px' }}>
        <img
          key={activeStyle}
          src={activePreview.image}
          alt={activePreview.label}
          className="w-full h-full object-cover object-top transition-all duration-500"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>

        {/* Active style info bottom-left */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div>
            <span className="block text-white font-bold text-lg leading-tight">{activePreview.label}</span>
            <span className="block text-white/75 text-xs mt-0.5 max-w-[220px] leading-relaxed">{activePreview.description}</span>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${activePreview.badgeStyle}`}>
            {activePreview.badge}
          </span>
        </div>

        {/* Selected checkmark */}
        {selectedStyle === activeStyle && (
          <div className="absolute top-3 right-3 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
            <i className="ri-check-line text-white text-sm"></i>
          </div>
        )}
      </div>

      {/* Style thumb row */}
      <div className="grid grid-cols-4 gap-2 px-5 pb-5">
        {STYLE_PREVIEWS.map((s) => {
          const isSelected = selectedStyle === s.id;
          const isHovered = hoveredStyle === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onSelectStyle(s.id)}
              onMouseEnter={() => setHoveredStyle(s.id)}
              onMouseLeave={() => setHoveredStyle(null)}
              className={`relative rounded-xl overflow-hidden transition-all duration-200 cursor-pointer group ${
                isSelected
                  ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 scale-[1.03]'
                  : isHovered
                  ? 'ring-2 ring-gray-300 dark:ring-gray-600 scale-[1.02]'
                  : ''
              }`}
              style={{ height: '60px' }}
              title={s.label}
            >
              <img
                src={s.image}
                alt={s.label}
                className="w-full h-full object-cover object-top"
              />
              {/* Dark tint */}
              <div className={`absolute inset-0 transition-all duration-200 ${
                isSelected ? 'bg-black/20' : 'bg-black/35 group-hover:bg-black/15'
              }`}></div>
              {/* Icon overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
                <i className={`${s.icon} text-sm text-white drop-shadow`}></i>
                <span className="text-white text-[9px] font-semibold drop-shadow whitespace-nowrap">{s.label}</span>
              </div>
              {/* Selected ring dot */}
              {isSelected && (
                <div className="absolute top-1 right-1 w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white" style={{ fontSize: '8px' }}></i>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
